import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

interface MapPickerProps {
  latitude?: number
  longitude?: number
  onChange: (lat: number, lng: number) => void
}

// Centro predefinito: Ancona
const DEFAULT_CENTER: [number, number] = [43.6158, 13.5189]
const DEFAULT_ZOOM = 10

/**
 * Componente per selezionare coordinate cliccando sulla mappa
 * Usato nell'admin per scegliere la posizione di un locale
 */
export default function MapPicker({ latitude, longitude, onChange }: MapPickerProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searching, setSearching] = useState(false)

  // Determina centro iniziale
  const hasCoords = latitude && longitude && !isNaN(latitude) && !isNaN(longitude)
  const initialCenter: [number, number] = hasCoords
    ? [latitude, longitude]
    : DEFAULT_CENTER

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return

    // Inizializza mappa
    const map = L.map(mapRef.current, {
      center: initialCenter,
      zoom: hasCoords ? 15 : DEFAULT_ZOOM,
    })

    // Tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map)

    // Marker iniziale se ci sono coordinate
    if (hasCoords) {
      markerRef.current = L.marker([latitude, longitude], {
        draggable: true
      }).addTo(map)

      // Gestisci drag del marker
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current?.getLatLng()
        if (pos) {
          onChange(pos.lat, pos.lng)
        }
      })
    }

    // Click sulla mappa per posizionare/spostare marker
    map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng

      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng])
      } else {
        markerRef.current = L.marker([lat, lng], {
          draggable: true
        }).addTo(map)

        markerRef.current.on('dragend', () => {
          const pos = markerRef.current?.getLatLng()
          if (pos) {
            onChange(pos.lat, pos.lng)
          }
        })
      }

      onChange(lat, lng)
    })

    mapInstanceRef.current = map

    return () => {
      map.remove()
      mapInstanceRef.current = null
    }
  }, [])

  // Aggiorna marker quando cambiano le coordinate dall'esterno
  useEffect(() => {
    if (!mapInstanceRef.current) return

    if (hasCoords && markerRef.current) {
      markerRef.current.setLatLng([latitude, longitude])
    }
  }, [latitude, longitude])

  // Ricerca indirizzo con Nominatim (OpenStreetMap)
  const handleSearch = async () => {
    if (!searchQuery.trim() || !mapInstanceRef.current) return

    setSearching(true)
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=it&limit=1`
      )
      const results = await response.json()

      if (results.length > 0) {
        const { lat, lon } = results[0]
        const newLat = parseFloat(lat)
        const newLng = parseFloat(lon)

        // Centra mappa
        mapInstanceRef.current.setView([newLat, newLng], 16)

        // Posiziona marker
        if (markerRef.current) {
          markerRef.current.setLatLng([newLat, newLng])
        } else {
          markerRef.current = L.marker([newLat, newLng], {
            draggable: true
          }).addTo(mapInstanceRef.current)

          markerRef.current.on('dragend', () => {
            const pos = markerRef.current?.getLatLng()
            if (pos) {
              onChange(pos.lat, pos.lng)
            }
          })
        }

        onChange(newLat, newLng)
      }
    } catch (error) {
      console.error('Errore ricerca indirizzo:', error)
    } finally {
      setSearching(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Barra ricerca */}
      <div className="flex gap-2">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Cerca indirizzo..."
          className="flex-1 px-4 py-2 bg-night border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gold"
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={searching}
          className="px-4 py-2 bg-gold text-night rounded-lg font-medium hover:bg-gold-light transition-colors disabled:opacity-50"
        >
          {searching ? '...' : 'Cerca'}
        </button>
      </div>

      {/* Mappa */}
      <div
        ref={mapRef}
        className="w-full h-64 rounded-lg overflow-hidden border border-gray-700"
      />

      {/* Istruzioni */}
      <p className="text-xs text-gray-500">
        Clicca sulla mappa per posizionare il marker, oppure cerca un indirizzo.
        Puoi trascinare il marker per regolare la posizione.
      </p>
    </div>
  )
}
